import { addDays } from '../../../utils/localDate';
/**
 * This module is evaluated once at import, so it holds i18n *keys*, never
 * translated strings. A `t()` call here would resolve at module load and then
 * never react to a language change — which is exactly how the planner ended up
 * rendering French labels inside an English app. Consumers translate at render.
 */
export const MEALS = [
  // Breakfast is available every day. `weekendOnly` is kept (all false) so the
  // `meal.weekendOnly && di < 5` gating sites become no-ops rather than needing
  // per-site edits; it can be removed in a later cleanup.
  { id: 'b', labelKey: 'thisWeek.mealTypes.breakfast', mealType: 'breakfast' as const, weekendOnly: false },
  { id: 'l', labelKey: 'thisWeek.mealTypes.lunch',     mealType: 'lunch'     as const, weekendOnly: false },
  { id: 'd', labelKey: 'thisWeek.mealTypes.dinner',    mealType: 'dinner'    as const, weekendOnly: false },
] as const;

export const DAY_PROFILE = [
  { tagKey: 'thisWeek.dayProfile.mon.tag', noteKey: 'thisWeek.dayProfile.mon.note', accent: '#c47b3c', maxT: 30 },
  { tagKey: 'thisWeek.dayProfile.tue.tag', noteKey: 'thisWeek.dayProfile.tue.note', accent: '#5d7c43', maxT: 40 },
  { tagKey: 'thisWeek.dayProfile.wed.tag', noteKey: 'thisWeek.dayProfile.wed.note', accent: '#3a78a0', maxT: 45 },
  { tagKey: 'thisWeek.dayProfile.thu.tag', noteKey: 'thisWeek.dayProfile.thu.note', accent: '#7c5c9e', maxT: 60 },
  { tagKey: 'thisWeek.dayProfile.fri.tag', noteKey: 'thisWeek.dayProfile.fri.note', accent: '#c4553c', maxT: 90 },
  { tagKey: 'thisWeek.dayProfile.sat.tag', noteKey: 'thisWeek.dayProfile.sat.note', accent: '#c47b3c', maxT: 60 },
  { tagKey: 'thisWeek.dayProfile.sun.tag', noteKey: 'thisWeek.dayProfile.sun.note', accent: '#85746b', maxT: 50 },
] as const;

// Fan alternative labels and their accent colors
export const FAN_REASONS = [
  { key: 'fast',    labelKey: 'fanReasons.fast',         accent: '#3a78a0' },
  { key: 'fridge',  labelKey: 'fanReasons.fridge',       accent: '#5d7c43' },
  { key: 'balance', labelKey: 'fanReasons.balanceLabel', accent: '#7c5c9e' },
  { key: 'new',     labelKey: 'fanReasons.newLabel',     accent: '#c4553c' },
  { key: 'fav',     labelKey: 'fanReasons.fav',          accent: '#c47b3c' },
] as const;

/** The accompaniment list's angles (#746) — `SIDE_ANGLES` of sideDish.ts, with their label and stripe. */
export const SIDE_REASONS = [
  { key: 'fridge',   labelKey: 'fanReasons.fridge',         accent: '#5d7c43' },
  { key: 'together', labelKey: 'sideReasons.togetherLabel', accent: '#3a78a0' },
  { key: 'balance',  labelKey: 'sideReasons.balanceLabel',  accent: '#7c5c9e' },
  { key: 'new',      labelKey: 'fanReasons.newLabel',       accent: '#c4553c' },
] as const;

export const slotKey = (mi: number, di: number): number => mi * 7 + di;
export const rowOf = (idx: number): number => Math.floor(idx / 7);
export const dayOf = (idx: number): number => idx % 7;

// Convert existing dayOfWeek (Sunday=0) to design di (Monday=0)
export const dowToDi = (dow: number): number => (dow + 6) % 7;
export const diToDow = (di: number): number => (di + 1) % 7;

/**
 * How long the generation « thinks » before the cascade — one value for both
 * layouts, because one module-level timeline serves both. It is the length of
 * the « Les affinités » panel below plus its exit, so the panel has closed
 * before the first cell lands.
 */
export const THINK_MS = 3600;

/**
 * « Les affinités » (GenerationAffinityPanel), in ms from the start of the
 * thinking phase. Three candidates, one every AFFINITY_STEP_MS; within each,
 * the matching facts light AFFINITY_HIT_MS apart. The panel plays these on its
 * own timers — the Redux timeline only owns THINK_MS, so cancelling stays a
 * single clearTimeout.
 */
export const AFFINITY_FIRST_MS = 300;
export const AFFINITY_STEP_MS = 900;
export const AFFINITY_HIT_DELAY_MS = 160;
export const AFFINITY_HIT_MS = 130;
export const AFFINITY_METER_DELAY_MS = 200;
export const AFFINITY_GONE_MS = 700;
export const AFFINITY_WON_MS = 640;
export const AFFINITY_COUNT_MS = 2600;
export const AFFINITY_PANEL_OUT_MS = 3300;
/** No runners-up (tiny library): the facts light one after another instead. */
export const AFFINITY_FACT_STEP_MS = 600;
export const STAG_MS_DESKTOP  = 130;
export const STAG_MS_MOBILE   = 150;

// Animation
export const EASE_ENTRANCE = [0.22, 0.7, 0.25, 1] as const;
export const SHEET_DURATION = 360;

/**
 * Day i18n keys, Monday-first to match the grid's day index (`di`).
 *
 * Note the deliberate mismatch: the `thisWeek.dayShort` / `thisWeek.dayNames`
 * objects in the locale files are keyed Sunday-first, while these arrays are
 * indexed Monday-first. Do not "align" them by reordering — an off-by-one here
 * shifts every column label by a day and still renders plausibly, so it will
 * not look broken.
 */
export const DAY_SHORT_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const DAY_FULL_KEYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

/** `t()` key for a day index (0 = Monday). */
export const dayShortKey = (di: number): string => `thisWeek.dayShort.${DAY_SHORT_KEYS[di]}`;
export const dayFullKey = (di: number): string => `thisWeek.dayNames.${DAY_FULL_KEYS[di]}`;

// Reveal order for cascade: Mon→Sun, dinner before lunch within day, breakfast last (weekends)
export function revealOrder(selections: number[]): number[] {
  const order: number[] = [];
  for (let di = 0; di < 7; di++) {
    // Souper (mi=2) first
    const souper = slotKey(2, di);
    if (selections.includes(souper)) order.push(souper);
    // Dîner (mi=1) second
    const diner = slotKey(1, di);
    if (selections.includes(diner)) order.push(diner);
    // Déjeuner (mi=0) last — weekends only
    const dejeuner = slotKey(0, di);
    if (selections.includes(dejeuner)) order.push(dejeuner);
  }
  return order;
}

/**
 * The Monday `delta` weeks away from `weekStart`.
 *
 * Formatted from the local date fields rather than `toISOString()`: local
 * midnight east of Greenwich serialises to the *previous* UTC day, which would
 * walk the planner back a day on every week change there.
 */
export function shiftWeek(weekStart: string, delta: number): string {
  return addDays(weekStart, delta * 7);
}
